<script setup lang="ts">
	import { useClipboard, useShare } from '@vueuse/core'
	import { TimeOut } from '@/services/apis/timeout'

	const { isMobile } = useMobile()
	const { copy, isSupported: supportsCopy, copied } = useClipboard()
	const { share, isSupported: supportsShare } = useShare()
	defineProps<{
		isOpen?: boolean
	}>()
	const emits = defineEmits<{
		(arg: 'update:isOpen', param: boolean): void
	}>()

	const isActivitiesOpen = ref(false)
	const supportId = shallowRef('call')
	const { getSupportGroup, getSupportContact } = useStore()
	const whatsappContacts = computed(() => getSupportGroup('whatsapp'))
	const support = computed(() => [
		{
			label: 'Call',
			icon: 'phone',
			id: 'call',
			links: [
				{
					linkText: getSupportContact('call')?.linkText || '',
					link: getSupportContact('call')?.link || '',
					icon: 'phone',
					labelledBy: 'tel'
				}
			]
		},
		{
			label: 'Chat',
			icon: 'whatsapp',
			id: 'chat',
			links: [
				{
					linkText: whatsappContacts.value[0]?.linkText || '',
					link: whatsappContacts.value[0]?.link || '',
					icon: 'whatsapp',
					target: '_blank',
					labelledBy: 'tel'
				},
				{
					linkText: whatsappContacts.value[1]?.linkText || '',
					link: whatsappContacts.value[1]?.link || '',
					icon: 'whatsapp',
					target: '_blank',
					labelledBy: 'tel'
				}
			]
		},
		{
			id: 'intercom_custom_btn',
			icon: 'chat',
			label: 'Web Chat'
		}
	])
	const currentSupport = computed(() => {
		return support.value.find(item => item.id === supportId.value)?.links ?? []
	})
	const manager = computed(() => getSupportContact('manager'))
	const openActivitiesOnMobile = () => {
		isActivitiesOpen.value = !isActivitiesOpen.value
		emits('update:isOpen', isActivitiesOpen.value)
	}
	const actionText = shallowRef('')
	const copyItem = async () => {
		if (!supportsCopy.value) {
			useNotification().alert({
				status: 400,
				message: 'Copy to clipboard is not supported on this device'
			})
			return
		}
		resolve(async () => {
			await copy(manager.value?.line || '')
			actionText.value = 'Copied'
			useNotification().alert({
				status: 200,
				message: 'Copied to clipboard'
			})
		})
	}
	const shareItem = async () => {
		if (!supportsShare.value) {
			useNotification().alert({
				status: 400,
				message: 'Share is not supported on this device'
			})
			return
		}
		resolve(async () => {
			share({
				title: 'Care Co-ordinator',
				text: `Get in touch with your care co-ordinator. ${manager.value?.line || ''}`
			})
		})
	}

	const hoverAction = async (arg: string, type: 'enter' | 'leave') => {
		if (type === 'enter') {
			actionText.value = arg
		} else {
			await TimeOut(10)
			actionText.value = ''
		}
	}

	onMounted(() => {
		isActivitiesOpen.value = !isMobile.value
	})
</script>

<template>
	<aside
		class="fixed right-0 top-20 z-[100] h-full max-w-[332px] border-l bg-white py-6 transition-[1s] ease-in-out md:py-12 xl:w-full xl:min-w-[332px]"
		:class="{
			'w-0': !isActivitiesOpen,
			'w-[332px]': isActivitiesOpen
		}"
	>
		<section class="h-full overflow-y-auto">
			<button
				type="button"
				class="mobile-controller absolute -left-10 top-5 z-[10] rounded-bl-[6px] rounded-tl-[6px] border border-b-transparent bg-white p-3 xl:hidden"
				@click="openActivitiesOnMobile"
			>
				<lazy-tools-icon icon="list" dir="line" path-class="fill-primary-2 " :height="15" :width="15" />
			</button>

			<div class="z-50 h-[inherit] overflow-y-auto">
				<section id="customer-support" class="border-b px-4 pb-[30px]">
					<div
						class="mt-6 min-w-[272px] items-start rounded-[10px] border-[0.6px] border-purple-300 bg-gray-50 px-[16px] py-[20px]"
					>
						<div class="mb-4 flex flex-col gap-2">
							<img
								:height="40"
								loading="lazy"
								:width="40"
								src="@/assets/images/png/support-40x.png"
								alt="customer support"
								class="rounded-full border-2 border-purple-400"
								:preview="false"
							/>
							<div>
								<h4 class="text-lg font-semibold tracking-wide">Olivia Onuoha</h4>
								<p class="pt-[1px] text-xs font-medium leading-[140%]">
									Reach Olivia for general support
								</p>
							</div>
						</div>

						<div
							class="invisible-scrollbar mt-5 flex items-center gap-x-2 overflow-x-auto border-t border-purple-50"
						>
							<button
								v-for="(item, index) in support"
								:id="item?.id ?? ''"
								:key="`account-info-${index}`"
								type="button"
								class="group inline-flex flex-none items-center gap-x-1 rounded-[200px] p-[8px_12px] text-xs font-medium transition-all hover:bg-purple-500 hover:text-white"
								:class="{
									'bg-purple-500 text-white': supportId === item.id,
									'bg-gray-300 text-gray-600': supportId !== item.id
								}"
								@click="supportId = item.id !== 'intercom_custom_btn' ? item.id : ''"
							>
								<tools-icon
									dir="communication"
									:icon="String(item.icon)"
									:height="14"
									:path-class="`group-hover:stroke-white transition ease-linear ${
										supportId === item.id ? 'stroke-white' : ''
									}`"
									:svg-class="`${item.id === 'intercom_custom_btn' ? 'group-hover:hidden' : ''}`"
								/>
								<tools-icon
									dir="communication"
									icon="chat-white"
									:height="14"
									:width="14"
									path-class=" transition ease-linear"
									:svg-class="`${
										item.id === 'intercom_custom_btn' ? 'group-hover:block' : ''
									} hidden`"
								/>
								<span>{{ item?.label ?? '' }}</span>
							</button>
						</div>

						<ul
							class="custom-transition-2 mb-0 mt-5 flex flex-col space-y-2 overflow-y-auto border-t"
							:class="[supportId === '' ? 'max-h-[0]' : 'max-h-[200px]']"
						>
							<template v-for="(link, i) in currentSupport" :key="'support-' + i">
								<li class="group mt-3 flex cursor-pointer items-center gap-3">
									<div
										class="flex h-[24px] w-[24px] items-center justify-center rounded-full bg-gray-300 transition ease-linear group-hover:bg-purple-500"
									>
										<tools-icon
											:icon="link.icon"
											dir="communication"
											:height="11.67"
											:width="11.65"
											path-class="group-hover:stroke-white transition ease-linear"
										/>
									</div>
									<a
										:href="link.link"
										class="ease text-sm font-extralight capitalize tracking-wide text-gray-500 transition group-hover:text-purple-500"
									>
										{{ link.linkText }}
									</a>
								</li>
							</template>
						</ul>
					</div>
				</section>

				<section id="updates" class="my-3 px-4 py-8">
					<div class="content-section-header space-y-2 tracking-wide">
						<h3 class="my-0 text-[20px] font-[500]">Updates</h3>
						<p class="text-sm text-gray-500">Here are your updates for today</p>
					</div>
					<div class="overflow-y-auto sm:max-h-[500px]">
						<layout-notification-activities class="mt-8" :limit="5">
							<template #empty-placeholder>
								<div class="flex w-full flex-col items-center justify-center">
									<div
										class="flex h-[83.33px] w-[83.33px] items-center justify-center rounded-full bg-gray-300 opacity-[0.4]"
									>
										<lazy-tools-icon icon="refresh" path-class="fill-gray-500" dir="line" />
									</div>
									<p class="mt-3 text-gray-400">You have no updates yet</p>
								</div>
							</template>
						</layout-notification-activities>
					</div>
				</section>
			</div>
		</section>
	</aside>
</template>
